import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import extractStringNodes from '../../utils/extract-string-nodes.esm.js';
import ownerDocument from '../../utils/owner-document.esm.js';
import TextFormatter from '../text-formatter/text-formatter.esm.js';
import { text2HTML } from '../text-formatter/utils.esm.js';

/* eslint-disable @typescript-eslint/no-explicit-any */

// We only handle element & text node.
var ELEMENT_NODE = 1;
var TEXT_NODE = 3;
var COMMENT_NODE = 8;
var getGhostContainer = function getGhostContainer(id) {
  {
    var identifier = "ellipsis-ghost-container-".concat(id);
    var _document = ownerDocument();
    var container = _document.getElementById(identifier);
    if (!container) {
      container = _document.createElement('div');
      container.setAttribute('id', identifier);
      container.setAttribute('aria-hidden', 'true');
      _document.body.appendChild(container);
    }
    return container;
  }
};
function removeGhostContainer(id) {
  var container = getGhostContainer(id);
  var document = ownerDocument();
  if (document && container) {
    document.body.removeChild(container);
  }
}
function pxToNumber(value) {
  if (!value) return 0;
  var match = value.match(/^\d*(\.\d*)?/);
  return match ? Number(match[0]) : 0;
}
function styleToString(style) {
  // There are some different behavior between Firefox & Chrome.
  // We have to handle this ourself.
  var styleNames = Array.prototype.slice.apply(style);
  return styleNames.map(function (name) {
    return "".concat(name, ": ").concat(style.getPropertyValue(name), ";");
  }).join('');
}
var measure = (function (id, wrapperElement, option, content, fixedContent, ellipsisStr) {
  var rows = option.rows,
    _option$suffix = option.suffix,
    suffix = _option$suffix === void 0 ? '' : _option$suffix;
  var contentList = extractStringNodes(React.Children.toArray(content));
  var originalContent = "".concat(contentList).concat(suffix);
  var ghostContainer = getGhostContainer(id);
  var childElement = wrapperElement !== null && wrapperElement !== void 0 && wrapperElement.childNodes[0] && wrapperElement.childNodes[0].nodeType === ELEMENT_NODE ? wrapperElement.childNodes[0] : undefined;
  var paragraphElement = childElement && childElement.childNodes[0] && childElement.childNodes[0].nodeType === ELEMENT_NODE ? childElement.childNodes[0] : undefined;
  var paragraphClassName = paragraphElement ? paragraphElement.className : '';
  var childElementClassName = childElement ? childElement.className : '';
  var childElementStyle = childElement ? window.getComputedStyle(childElement) : undefined;
  // Get origin style
  var wrapperElementStyle = window.getComputedStyle(wrapperElement);
  var originCSS = styleToString(wrapperElementStyle);
  var lineHeight = childElementStyle ? pxToNumber(childElementStyle.fontSize) : pxToNumber(wrapperElementStyle.fontSize);
  var maxHeight = Math.round(lineHeight * (rows + 1) + pxToNumber(wrapperElementStyle.paddingTop) + pxToNumber(wrapperElementStyle.paddingBottom));

  // Set shadow
  ghostContainer.setAttribute('style', originCSS);
  ghostContainer.style.position = 'fixed';
  ghostContainer.style.left = '0';
  ghostContainer.style.height = 'auto';
  ghostContainer.style.minHeight = 'auto';
  ghostContainer.style.maxHeight = 'auto';
  ghostContainer.style.top = '-999999px';
  ghostContainer.style.zIndex = '-1000';

  // clean up css overflow
  ghostContainer.style.textOverflow = 'clip';
  ghostContainer.style.whiteSpace = 'normal';
  ghostContainer.style.webkitLineClamp = 'none';

  // Render in the fake container
  render(/*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    key: "ghost-container-".concat(id),
    className: childElementClassName
  }, /*#__PURE__*/React.createElement(TextFormatter, {
    id: "ghost-container-section-".concat(id)
  }, originalContent)), /*#__PURE__*/React.createElement("span", {
    className: childElementClassName
  }, fixedContent)), ghostContainer); // wrap in an div for old version react

  // Check if ellipsis in measure div is height enough for content
  function inRange() {
    return ghostContainer.offsetHeight < maxHeight;
  }

  // Skip ellipsis if already match
  if (inRange()) {
    unmountComponentAtNode(ghostContainer);
    return {
      content: content,
      text: ghostContainer.innerHTML,
      ellipsis: false
    };
  }

  // We should clone the childNode since they're controlled by React and we can't reuse it without warning
  var childNodes = Array.prototype.slice.apply(ghostContainer.childNodes[0].childNodes[0].cloneNode(true).childNodes).filter(function (_ref) {
    var nodeType = _ref.nodeType;
    return nodeType !== COMMENT_NODE;
  });
  var fixedNodes = Array.prototype.slice.apply(ghostContainer.childNodes[0].childNodes[1].cloneNode(true).childNodes);
  unmountComponentAtNode(ghostContainer);

  // ========================= Find match ellipsis content =========================
  var ellipsisChildren = [];
  ghostContainer.innerHTML = '';

  // Create origin content holder
  var ellipsisContentHolder = document.createElement('span');
  ellipsisContentHolder.className = childElementClassName;
  ghostContainer.appendChild(ellipsisContentHolder);
  var ellipsisTextNode = document.createTextNode(ellipsisStr + suffix);
  ellipsisContentHolder.appendChild(ellipsisTextNode);
  fixedNodes.forEach(function (childNode) {
    ghostContainer.appendChild(childNode);
  });

  // Append before fixed nodes
  function appendChildNode(node) {
    ellipsisContentHolder.insertBefore(node, ellipsisTextNode);
  }
  var appendingHtml = '';
  function getAppendingHTML(parentElement) {
    if (parentElement !== null && parentElement !== void 0 && parentElement.nextElementSibling) {
      var _parentElement$nextEl;
      var nextElementSibling = parentElement === null || parentElement === void 0 ? void 0 : parentElement.nextElementSibling;
      appendingHtml = parentElement === null || parentElement === void 0 ? void 0 : (_parentElement$nextEl = parentElement.nextElementSibling) === null || _parentElement$nextEl === void 0 ? void 0 : _parentElement$nextEl.outerHTML;
      nextElementSibling && nextElementSibling.remove();
    }
    return appendingHtml;
  }

  // Get maximum text
  function measureText(parentElement, fullText) {
    var startLoc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var endLoc = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : fullText.length;
    var lastSuccessLoc = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
    var midLoc = Math.floor((startLoc + endLoc) / 2);
    var currentText = fullText.slice(0, midLoc);
    var appendingHTML = getAppendingHTML(parentElement);
    parentElement.innerHTML = text2HTML(currentText + ellipsisStr + suffix, paragraphClassName, appendingHTML);
    if (startLoc >= endLoc - 1) {
      // Loop when step is small
      for (var step = endLoc; step >= startLoc; step -= 1) {
        var currentStepText = fullText.slice(0, step);
        parentElement.innerHTML = text2HTML(currentStepText + ellipsisStr + suffix, paragraphClassName, appendingHTML);
        if (inRange() || !currentStepText) {
          return step === fullText.length ? {
            finished: false,
            reactNode: fullText
          } : {
            finished: true,
            reactNode: currentStepText
          };
        }
      }
    }
    if (inRange()) {
      return measureText(parentElement, fullText, midLoc, endLoc, midLoc);
    }
    return measureText(parentElement, fullText, startLoc, midLoc, lastSuccessLoc);
  }
  function measureNode(childNode, index) {
    var type = childNode.nodeType;
    if (type === ELEMENT_NODE) {
      // We don't split element, it will keep if whole element can be displayed.
      appendChildNode(childNode);
      if (inRange()) {
        return {
          finished: false,
          reactNode: contentList[index]
        };
      }

      // Clean up if can not pull in
      ellipsisContentHolder.removeChild(childNode);
      // resolve measureText using the originalContent
      return measureText(ellipsisContentHolder, originalContent);
    }
    if (type === TEXT_NODE) {
      var fullText = childNode.textContent || '';
      var textNode = document.createTextNode(fullText);
      appendChildNode(textNode);
      return measureText(textNode.parentElement, fullText);
    }

    // Not handle other type of content
    return {
      finished: false,
      reactNode: null
    };
  }
  childNodes.some(function (childNode, index) {
    var _measureNode = measureNode(childNode, index),
      reactNode = _measureNode.reactNode,
      finished = _measureNode.finished;
    if (reactNode) {
      ellipsisChildren.push(reactNode);
    }
    return finished;
  });
  return {
    content: ellipsisChildren,
    text: ghostContainer.innerHTML,
    ellipsis: true
  };
});

export default measure;
export { getGhostContainer, removeGhostContainer };
//# sourceMappingURL=util.esm.js.map
